import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Stack,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import countryCode from "country-codes-list";
import CustomLocationInput from "./CustomLoation";
import { addDoc, collection } from "firebase/firestore";
import { useFirebase } from "../hooks/app-state";

const myCountryCodesObject = countryCode.all().map((country) => ({
  label: `+${country.countryCallingCode} ${country.countryNameEn} `,
  code: `+${country.countryCallingCode}`,
  flagcode: `${country.countryCode}`,
}));

export default function ResponsiveForms() {
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form1Data, setForm1Data] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    countryCode: "",
  });

  const [form2Data, setForm2Data] = useState({
    companyName: "",
    email: "",
    countryCode: "",
    phone: "",
    companyurl: "",
  });

  const [form1Errors, setForm1Errors] = useState({
    name: false,
    email: false,
    location: false,
  });

  const [form2Errors, setForm2Errors] = useState({
    companyName: false,
    email: false,
    companyurl: false,
  });

  const handleChangeForm1 = (name, value) => {
    setForm1Data((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeForm2 = (name, value) => {
    setForm2Data((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email validation
    return email && !emailRegex.test(email); // Return true if email is invalid
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[+]?[1-9]\d{1,14}$/; // International phone number validation
    return phone && !phoneRegex.test(phone); // Return true if phone is invalid
  };

  const validateRequiredFields = (data, requiredFields) => {
    const errors = {};
    requiredFields.forEach((field) => {
      errors[field] = data[field] === ""; // Check if field is empty
    });
    return errors;
  };

  const handleSubmitForm1 = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);

      let errors = validateRequiredFields(form1Data, [
        "name",
        "email",
        "location",
      ]);
      errors.email = validateEmail(form1Data.email);
      errors.phone = validatePhoneNumber(form1Data.phone);
      setForm1Errors(errors);

      if (!Object.values(errors).some((error) => error)) {
        const phoneNumber = `${form1Data.countryCode}${form1Data.phone}`;
        const { countryCode, phone, ...formDataCopy } = form1Data;
        const finalFormData = { ...formDataCopy, phone: phoneNumber };

        await addDoc(collection(db, "userDetails"), finalFormData);
        navigate("/thank-you");
      }
    } catch (error) {
      console.error(
        "An error occurred during form submission (Form 1):",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm2 = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);

      let errors = validateRequiredFields(form2Data, [
        "companyName",
        "email",
        "companyurl",
      ]);
      errors.email = validateEmail(form2Data.email);
      errors.phone = validatePhoneNumber(form2Data.phone);
      setForm2Errors(errors);

      if (!Object.values(errors).some((error) => error)) {
        const phoneNumber = `${form2Data.countryCode}${form2Data.phone}`;
        const { countryCode, phone, ...formDataCopy } = form2Data;
        const finalFormData = { ...formDataCopy, phone: phoneNumber };
        console.log("finalFormData", finalFormData);
        
        await addDoc(collection(db, "companyDetails"), finalFormData);
        navigate("/thank-you");
      }
    } catch (error) {
      console.error(
        "An error occurred during form submission (Form 2):",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#E6E8EC", pt: 15, pb: 15 }} id="about-us">
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "white" }} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "column",
            xl: "row", // Ensuring row layout for larger screens
          }, // Stack vertically on small screens, horizontally otherwise
          gap: 4,
          width: { lg: "80%", md: "90%", xs: "95%" }, // Box takes 80% of the parent width
          margin: "0 auto", // Center the Box itself horizontally
          alignItems: "center", // Center children on the cross-axis
          justifyContent: "center", // Center children on the main axis
        }}
      >
        {/* First Form */}
        <Form1
          form1Data={form1Data}
          form1Errors={form1Errors}
          handleChangeForm1={handleChangeForm1}
          handleSubmitForm1={handleSubmitForm1}
          myCountryCodesObject={myCountryCodesObject}
        />

        <Box
          sx={{
            width: { xs: 0, sm: 2, md: 4, lg: 50 },
          }}
        />

        {/* Second Form */}
        <Form2
          form2Data={form2Data}
          form2Errors={form2Errors}
          handleChangeForm2={handleChangeForm2}
          handleSubmitForm2={handleSubmitForm2}
          myCountryCodesObject={myCountryCodesObject}
        />
      </Box>
    </Box>
  );
}

const Form1 = ({
  form1Data,
  form1Errors,
  handleChangeForm1,
  handleSubmitForm1,
  myCountryCodesObject,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: 4,
        borderRadius: 2,
        flex: 1,
        boxShadow: "0px 64px 64px -48px rgba(15, 15, 15, 0.1)",
        maxWidth: "60%",
      }}
    >
      <Typography
        fontWeight={"700"}
        fontSize={{
          xs: "19px",
          sm: "20px",
          md: "30px",
          lg: "40px",
          mb: 10,
        }}
        gutterBottom
      >
        Join the waitlist to access{" "}
        <span style={{ color: "#2361df" }}>EIRA</span>.
      </Typography>
      <form
        onSubmit={handleSubmitForm1}
        style={{
          marginTop: "50px",
          marginBottom: "40px",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          display: "flex",
        }}
      >
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography
            fontSize={14}
            sx={{ marginBottom: 1 }}
            fontFamily={"Poppins"}
            fontWeight={"400"}
          >
            Name{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <TextField
            placeholder="Enter your name here"
            name="name"
            value={form1Data.name}
            onChange={(e) => handleChangeForm1("name", e.target.value)}
            fullWidth
            required
            error={form1Errors.name}
            helperText={form1Errors.name ? "Name is required" : ""}
            sx={{ marginBottom: 2 }}
          />
        </Stack>
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Email{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <TextField
            placeholder="name@example.com"
            name="email"
            value={form1Data.email}
            onChange={(e) => handleChangeForm1("email", e.target.value)}
            fullWidth
            required
            error={form1Errors.email}
            helperText={form1Errors.email ? "Email is required" : ""}
            sx={{ marginBottom: 2 }}
          />
        </Stack>
        <Stack spacing={1} mb={2} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Phone Number
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <CountryCodeSelector
              value={form1Data.countryCode}
              onChangeValue={(val) => {
                handleChangeForm1("countryCode", val);
              }}
              countryCodes={myCountryCodesObject}
            />

            <TextField
              placeholder="Phone Number"
              name="phone"
              value={form1Data.phone}
              onChange={(e) => handleChangeForm1("phone", e.target.value)}
              fullWidth
              error={form1Errors.phone}
              helperText={form1Errors.phone ? "Invalid phone number" : ""}
              sx={{ marginBottom: 2 }}
            />
          </Box>
        </Stack>
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Location{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <CustomLocationInput
            handleInputValueChange={(section, field, value) => {
              handleChangeForm1("location", value);
            }} // Function to handle input changes
            componentLabel={null} // Label to show for the input
            required={true} // Whether the input is required
            selectedPlace={form1Data.location} // Initial value for the input
            field="location" // The key used in the handleInputValueChange function
          />
        </Stack>
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: "rgba(35, 97, 223, 1)",
            width: "50%",
            borderRadius: "4px",
            py: 1.5,
          }}
        >
          <Typography
            sx={{ textTransform: "none" }}
            fontFamily={"SF Pro Display"}
            fontWeight={"700"}
            fontSize={{
              lg: "16px",
              md: "15px",
              sm: "13px",
              xs: "12px",
            }}
            letterSpacing={0.5}
          >
            Submit
          </Typography>
        </Button>
      </form>
    </Box>
  );
};

const Form2 = ({
  form2Data,
  form2Errors,
  handleChangeForm2,
  handleSubmitForm2,
  myCountryCodesObject,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: 4,
        borderRadius: 2,
        flex: 1,
        boxShadow: "0px 64px 64px -48px rgba(15, 15, 15, 0.1)",
        maxWidth: "60%",
      }}
    >
      <Typography
        fontWeight={"700"}
        fontSize={{
          xs: "19px",
          sm: "20px",
          md: "30px",
          lg: "40px",
          mb: 10,
        }}
        gutterBottom
      >
        Start Your <span style={{ color: "#2361df" }}>Partnership</span> Journey
      </Typography>
      <form
        onSubmit={handleSubmitForm2}
        style={{
          marginTop: "50px",
          marginBottom: "40px",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          display: "flex",
        }}
      >
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Company Name{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <TextField
            placeholder="Enter your company name here"
            name="companyName"
            value={form2Data.companyName}
            onChange={(e) => handleChangeForm2("companyName", e.target.value)}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
        </Stack>
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Email{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <TextField
            placeholder="name@example.com"
            name="email"
            value={form2Data.email}
            onChange={(e) => handleChangeForm2("email", e.target.value)}
            fullWidth
            required
            error={form2Errors.email}
            helperText={form2Errors.email ? "Email is required" : ""}
            sx={{ marginBottom: 2 }}
          />
        </Stack>
        <Stack spacing={1} mb={2} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Phone Number
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <CountryCodeSelector
              value={form2Data.countryCode}
              onChangeValue={(val) => {
                handleChangeForm2("countryCode", val);
              }}
              countryCodes={myCountryCodesObject}
            />
            <TextField
              placeholder="Phone Number"
              name="phone"
              value={form2Data.phone}
              onChange={(e) => handleChangeForm2("phone", e.target.value)}
              fullWidth
              error={form2Errors.phone}
              helperText={form2Errors.phone ? "Invalid phone number" : ""}
              sx={{ marginBottom: 2 }}
            />
          </Box>
        </Stack>
        <Stack spacing={1} mb={3} width={"100%"}>
          <Typography fontSize={14} sx={{ marginBottom: 1 }}>
            Website{" "}
            <span
              style={{
                color: "#ba1a1a",
              }}
            >
              *
            </span>
          </Typography>
          <TextField
            placeholder="Enter your website URL here"
            name="companyurl"
            value={form2Data.companyurl}
            onChange={(e) => handleChangeForm2("companyurl", e.target.value)}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
        </Stack>
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: "rgba(35, 97, 223, 1)",
            width: "50%",
            borderRadius: "4px",
            py: 1.5,
          }}
        >
          <Typography
            sx={{ textTransform: "none" }}
            fontFamily={"SF Pro Display"}
            fontWeight={"700"}
            fontSize={{
              lg: "16px",
              md: "15px",
              sm: "13px",
              xs: "12px",
            }}
            letterSpacing={0.5}
          >
            Submit
          </Typography>
        </Button>
      </form>
    </Box>
  );
};

const CountryCodeSelector = ({ value, onChangeValue, countryCodes }) => {
  return (
    <FormControl sx={{ marginBottom: 2, width: { xs: "50%", lg: "50%" } }}>
      <Autocomplete
        options={countryCodes}
        getOptionLabel={(option) => option.label || option.code} // Show country label or code
        value={countryCodes.find((country) => country.code === value) || null}
        onChange={(event, newValue) =>
          onChangeValue(newValue ? newValue.code : "")
        }
        renderInput={(params) => (
          <TextField {...params} placeholder="Search country" />
        )}
        isOptionEqualToValue={(option, value) => option.code === value.code} // Ensure correct selection
      />
    </FormControl>
  );
};
